import { AbstractRenderer, Alignment, CellPanel, ColumnComposition, ColumnCompositions, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { BenchmarkOption } from '@ddt-web/config';
import { TabData, TabUtil } from '@ddt-web/data-service';
import { NumberUtil } from '@ddt-web/generic-utils';
import * as cloneDeep from 'lodash/cloneDeep';
import * as isEmpty from 'lodash/isEmpty';

import { HoldingsColumnComposition } from '../../helpers';
import { RowUtils } from './row-utils';

export class PdfUtils {

	public static getHorizontalAlignmentBasedOnValue(stringValue: string): Alignment.Horizontal {
		return NumberUtil.isNumericalValue(stringValue) ? Alignment.Horizontal.Right : Alignment.Horizontal.Left;
	}

	public static getHorizontalAlignment(textAlign: string, defaultAlignment: Alignment.Horizontal = Alignment.Horizontal.Left): Alignment.Horizontal {
		if (textAlign === 'left') {
			return Alignment.Horizontal.Left;
		} else if (textAlign === 'right') {
			return Alignment.Horizontal.Right;
		} else if (textAlign === 'center') {
			return Alignment.Horizontal.Center;
		} else {
			return defaultAlignment;
		}
	}

	public static getSnapshotColumnProportions(noOfPortfolios: number): Array<number> {
		let columnProportions: Array<number>;
		switch (noOfPortfolios) {
			case 1:
				columnProportions = [50, 50];
				break;
			case 2:
				columnProportions = [61.2, 19.4, 19.4];
				break;
			case 3:
				columnProportions = [47.2, 17.6, 17.6, 17.6];
				break;
			case 4:
				columnProportions = [29.6, 17.6, 17.6, 17.6, 17.6];
				break;
			case 5:
				columnProportions = [20, 16, 16, 16, 16, 16];
				break;
			case 6:
				columnProportions = [22, 13, 13, 13, 13, 13, 13];
				break;
			case 7:
				columnProportions = [23, 11, 11, 11, 11, 11, 11, 11];
				break;
			default:
				columnProportions = [100];
		}
		return columnProportions;
	}

	public static getTabWithValidColumns(
		tab: TabData,
		selectedBenchmarkForCaptureRatio: string,
		benchmarkColumnId: string,
		snp500ColumnId: string,
		shouldFilterHeadersY: boolean = false
	): TabData {
		const excludeColumns = selectedBenchmarkForCaptureRatio === BenchmarkOption.BENCHMARK ?
			[snp500ColumnId] :
			[benchmarkColumnId];
		let updatedTab = cloneDeep(tab);

		if (!isEmpty(excludeColumns)) {
			updatedTab = TabUtil.getFilteredTabData(updatedTab, excludeColumns, shouldFilterHeadersY);
		}

		return updatedTab;
	}

	/** Converts or extracts list items into an array from unordered list html string
	 *
	 * @param {string} htmlAsString
	 * @return {string[]}  list items 
	 */
	public static convertUnorderedListHtmlStringToArray(htmlAsString: string): string[] {
		const domParsed = (new DOMParser()).parseFromString(htmlAsString, 'text/xml');
		if (domParsed.getElementsByTagName('ul')) {
			const liElements = Array.from(domParsed.querySelectorAll('li'));
			const listElementsHtmlContent = liElements.map(elem => elem.innerHTML.trim()).filter(elem => !!elem);
			return listElementsHtmlContent;
		}
		return [];
	}

	/**
	 * This method is used for building the row section elements of the cost and yield sections
	 * @param tabData the tab data of the report section
	 * @param columnCompositions the column compositions of the report section
	 * @param tableColumnProportions the column proportions array of the report section
	 * @returns the row elements of the section we want to build
	 */
	public static buildSectionElements(tabData: TabData, columnCompositions: ColumnCompositions, tableColumnProportions: number[]): AbstractRenderer<SVGGElement>[] {

		const tableHeaderCells = new HoldingsColumnComposition()
			.getSnapShotHeadingCompositions(tabData, tableColumnProportions);

		const tableHeaders = tableHeaderCells.map((config: ColumnComposition, configIndex: number) => {
			if (configIndex > 0) {
				return new CellPanel(
					[
						new StaticParagraphComponent(
							`PDF_${config.cells[0].formattedValue}_TITLE`,
							config.cells[0].formattedValue,
							{},
							{},
							['text-8', 'text-bold'])
					],
					{ proportion: config.widthProportion },
					config.horizontalAlignment,
					null,
					Alignment.Vertical.Middle
				);
			}
			return new CellPanel(
				[
					new StaticParagraphComponent(`PDF_${config.cells[0].formattedValue}_TITLE`, config.cells[0].formattedValue, {}, {})
				],
				{ proportion: config.widthProportion }
			);
		});

		const tableHeaderRow = new RowPanel(
			'Table Header Cells',
			tableHeaders,
			{},
			Alignment.Horizontal.Left,
			Alignment.Vertical.Bottom
		);

		const rows: Array<RowPanel> = RowUtils.buildRows(
			columnCompositions,
			'Row_' + tabData?.config?.id,
			'Subsection_' + tabData?.config?.id,
			['text-10', 'text-bold'],
			{ marginRight: 15 }
		);

		const sectionElements: AbstractRenderer<SVGGElement>[] = [];
		[tableHeaderRow, ...rows].forEach((row, index) => {
			sectionElements.push(new RowPanel(`Section item ${index}`, [row]));
		});

		return sectionElements;
	}
}
